import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, CreateReviewRequest } from '../../services/api';
import { Toast } from '../common/Toast';

interface ReviewFormProps {
  bookingId: string;
  expertId: string;
  expertEmail: string;
  onReviewSubmitted: () => void;
}

export function ReviewForm({ bookingId, expertId: _expertId, expertEmail, onReviewSubmitted }: ReviewFormProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating < 1 || rating > 5) {
      setNotification({
        message: t('reviews.invalidRating'),
        type: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      const reviewData: CreateReviewRequest = {
        bookingId,
        rating,
        comment,
      };
      
      await apiService.reviews.create(reviewData);
      
      setNotification({
        message: t('reviews.submitSuccess'),
        type: 'success',
      });
      
      onReviewSubmitted();
      
      // Reset form
      setRating(5);
      setComment('');
    } catch {
      setNotification({
        message: t('reviews.submitError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
      <h2 className="mb-4 break-words text-xl font-semibold text-stitch-fg">
        {t('reviews.leaveReview')} — {expertEmail}
      </h2>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="rating" className="block text-sm font-medium text-stitch-fg-soft mb-1">
            {t('reviews.rating')}
          </label>
          <div className="flex items-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="text-2xl focus:outline-none"
              >
                <span
                  className={`material-symbols-outlined text-3xl ${
                    star <= rating ? 'text-stitch-warning' : 'text-stitch-border'
                  }`}
                  style={{ fontVariationSettings: star <= rating ? "'FILL' 1, 'wght' 500" : "'wght' 400" }}
                >
                  star
                </span>
              </button>
            ))}
            <span className="ml-2 text-sm text-stitch-muted">({rating}/5)</span>
          </div>
        </div>
        
        <div className="mb-4">
          <label htmlFor="comment" className="block text-sm font-medium text-stitch-fg-soft mb-1">
            {t('reviews.comment')}
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="block w-full rounded-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
            placeholder={t('reviews.commentPlaceholder')}
          />
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="rounded-stitch-lg bg-stitch-primary px-5 py-2.5 font-semibold text-white shadow-md shadow-stitch-primary/20 transition hover:bg-stitch-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? t('common.loading') : t('reviews.submit')}
          </button>
        </div>
      </form>
      
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
} 